import { getOrderBrandName } from '../utilities/OrderHelper';
import { Button, Col, Dropdown, Row, Text } from "rsuite";
import { AirplaneFill, App, ArrowDown, ArrowRightSquare, BuildingFill, Calendar, CalendarFill, CardHeading, CardText, CarFront, Cash, ChatDotsFill, CreditCard, Envelope, GeoAltFill, PeopleFill, PersonCircle, RecordCircleFill, SuitcaseFill, TelephoneFill, CheckCircleFill, CarFrontFill } from "react-bootstrap-icons";
import { OrderDisplayProps, SPECIAL_REQUESTS } from "../my-type";
import { OrderActionType, OrderStatus, OrderType, UserRole } from "../my-enum";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authUserData } from "../service";
import { VehicleService as GeneratedVehicleService, callApi, configureApiClient } from "../api/client";
import { getSupplierById } from "../service/SupplierService";
import { getFlightLatestInfo, getOrder, setRelatedOrder as setOrderRelation, type FlightLatestInfo } from "../service/OrderService";
import { getUserById } from "../service/UserService";
import { TeamVehicle } from "../service/VehicleService";
import { hasRole } from "../util/roles";
//import styles from "../pages/style.module.css"


export default function OrderDisplay({ order, isShowDetail, userRole, onOrderAction, onOrderUpdated }: OrderDisplayProps) {

  const [isShowOrderDetail, setIsShowOrderDetail] = useState<boolean>(isShowDetail);
  const [teamVehicles, setTeamVehicles] = useState<TeamVehicle[]>([]);
  const [supplierName, setSupplierName] = useState<string | null>(null);
  const [creatorName, setCreatorName] = useState<string | null>(null);
  const [flightLatestInfo, setFlightLatestInfo] = useState<FlightLatestInfo | null>(null);
  const navigate = useNavigate();
  const user = authUserData();
  const canManageOrders = hasRole(user?.roleString, "Driver");

  const handleRelatedOrderChange = async (nextRelatedOrderID: string | null) => {
    const result = await setOrderRelation(order.orderID, nextRelatedOrderID);
    if (result.statusCode !== 200) {
      alert(result.message || "關聯訂單更新失敗");
      return;
    }

    const refreshedOrder = await getOrder(order.orderID);
    if (refreshedOrder) {
      onOrderUpdated?.(refreshedOrder);
    }

    alert(result.message || "關聯訂單更新成功");
  };

  useEffect(() => {
    const fetchTeamVehicles = async () => {
      if (!user?.team?.id) {
        setTeamVehicles([]);
        return;
      }

      try {
        configureApiClient({
          tokenProvider: () => authUserData()?.jwtToken ?? null,
        });
        const vehicles = await callApi(
          () => GeneratedVehicleService.getApiVehicleGetAllTeamVehicles(user.team.id),
          "Error fetching all team vehicles"
        );
        setTeamVehicles((vehicles as TeamVehicle[]) ?? []);
      } catch (error) {
        console.error("Error fetching all team vehicles:", error);
        setTeamVehicles([]);
      }
    };

    fetchTeamVehicles();
  }, [user?.team?.id]);

  const vehicleName =
    teamVehicles.find((element) => element.vehicleID === order.vehicleID)?.vehicleName ??
    order.vehicleID ??
    "";

  // Re-sync state when order or isShowDetail prop changes
  useEffect(() => {
    setIsShowOrderDetail(isShowDetail);
  }, [order, isShowDetail])

  // Fetch supplier name when supplierId exists
  useEffect(() => {
    const fetchSupplierName = async () => {
      if (order?.supplierId) {
        try {
          const supplier = await getSupplierById(order.supplierId);
          if (supplier) {
            setSupplierName(supplier.name);
          }
        } catch (error) {
          console.error('Error fetching supplier:', error);
        }
      } else {
        setSupplierName(null);
      }
    };
    fetchSupplierName();
  }, [order?.supplierId])

  useEffect(() => {
    const fetchCreatorName = async () => {
      if (!order?.creatorUID) {
        setCreatorName(null);
        return;
      }

      try {
        const creator = await getUserById(order.creatorUID);
        setCreatorName(creator?.displayName || creator?.userId || `${order.creatorUID}`);
      } catch (error) {
        console.error('Error fetching creator:', error);
        setCreatorName(`${order.creatorUID}`);
      }
    };

    fetchCreatorName();
  }, [order?.creatorUID])

  useEffect(() => {
    let isCancelled = false;

    const fetchFlightLatestInfo = async () => {
      if (!order?.orderID || order?.flightNo == null || order?.flightTime == null) {
        setFlightLatestInfo(null);
        return;
      }

      try {
        const latestInfo = await getFlightLatestInfo(order.orderID);
        if (!isCancelled) {
          setFlightLatestInfo(latestInfo);
        }
      } catch (error) {
        console.error("Error fetching flight latest info:", error);
        if (!isCancelled) {
          setFlightLatestInfo(null);
        }
      }
    };

    fetchFlightLatestInfo();

    return () => {
      isCancelled = true;
    };
  }, [order?.orderID, order?.flightNo, order?.flightTime, order?.terminal])

  // Validate order data to prevent Invalid URL errors
  if (!order || !order.orderID) {
    console.error('Invalid order data:', order);
    return <div style={{ padding: '15px', color: 'red' }}>訂單資料錯誤</div>;
  }

  function getOrderTypeKey(orderType: OrderType) {
    let keyStr: string = '';
    switch (orderType) {
      case 'in':
        keyStr = '接機';
        break
      case 'out':
        keyStr = '送機';
        break;
      case 'point_to_point':
        keyStr = '接送';
        break;
      default:
        keyStr = 'N/A';
    }

    return keyStr;
  }

  function getDisplayDateTimeString(dateTime: Date) {
    let parsedTime = new Date(dateTime);
    let strDate = parsedTime.toLocaleString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' });
    let strTime = parsedTime.toLocaleTimeString('zh-TW', { hourCycle: 'h23', hour: "2-digit", minute: "2-digit" });
    //console.log(strDate + ' ' + strTime);
    return strDate + ' ' + strTime;
  }

  function getTaipeiWallClockDateTimeString(dateTimeText: string) {
    const parsedTime = new Date(dateTimeText);
    if (Number.isNaN(parsedTime.getTime())) {
      return dateTimeText;
    }

    const year = parsedTime.getUTCFullYear();
    const month = String(parsedTime.getUTCMonth() + 1).padStart(2, "0");
    const day = String(parsedTime.getUTCDate()).padStart(2, "0");
    const hour = String(parsedTime.getUTCHours()).padStart(2, "0");
    const minute = String(parsedTime.getUTCMinutes()).padStart(2, "0");

    return `${year}/${month}/${day} ${hour}:${minute}`;
  }

  function getFlightStatusDisplay(info: FlightLatestInfo) {
    const isDepartureOrder = order.orderType === OrderType.送機;
    const scheduleTimeText = isDepartureOrder
      ? (info.scheduleDepartureTime ?? info.scheduleArrivalTime)
      : info.scheduleArrivalTime;
    const estimatedTimeText = isDepartureOrder
      ? (info.estimatedDepartureTime ?? info.scheduleDepartureTime ?? info.estimatedArrivalTime)
      : info.estimatedArrivalTime;
    const remark = isDepartureOrder
      ? (info.departureRemark ?? info.arrivalRemark ?? "")
      : (info.arrivalRemark ?? "");

    if (!scheduleTimeText || !estimatedTimeText || !remark) {
      return null;
    }

    const scheduleTime = new Date(scheduleTimeText);
    const estimatedTime = new Date(estimatedTimeText);
    const isScheduleChanged = scheduleTime.getTime() !== estimatedTime.getTime();
    const containsScheduleChange = remark.includes("SCHEDULE CHANGE");
    const containsOnTime = remark.includes("ON TIME");

    if (!containsScheduleChange && !containsOnTime) {
      return null;
    }

    if (!isScheduleChanged && !containsOnTime) {
      return null;
    }

    const color = isScheduleChanged && containsScheduleChange ? "red" : "black";

    return {
      color,
      estimatedTime: getTaipeiWallClockDateTimeString(estimatedTimeText),
      remark,
    };
  }

  const statusStyle = () => {

    let styleColor = {};
    switch (order.status) {
      case OrderStatus.派車中:
        styleColor = { color: 'green', fontWeight: 'bold' };
        break;
      case OrderStatus.已派車:
        styleColor = { color: 'orange', fontWeight: 'bold' };
        break;
      case OrderStatus.載客中:
        styleColor = { color: 'blue', fontWeight: 'bold' };
        break;
      default:
        styleColor = { color: 'GrayText' };
    }

    return styleColor;

  }

  const renderDriverActionButton = (isBlock: boolean) => {
    if (userRole === UserRole.Driver && order.driverUID === user?.id) {
      if (order.status === OrderStatus.已派車) {
        return <Button appearance="primary" block={isBlock} onClick={() => {
          onOrderAction(order, OrderActionType.Next);
        }}>
          出發載客
        </Button>
      }


      if (order.status === OrderStatus.已出發) {
        return <Button appearance="primary" block={isBlock} onClick={() => {
          onOrderAction(order, OrderActionType.Next);
          //setIsShowOrderDetail(false);
        }}>
          乘客上車
        </Button>


      }


      if (order.status === OrderStatus.載客中) {
        return <Button appearance="primary" block={isBlock} onClick={() => {
          onOrderAction(order, OrderActionType.Next);
          //setIsShowOrderDetail(false);
        }}>
          完成訂單
        </Button>

      }

    } else if (userRole === UserRole.Driver && order.driverUID === 0) {
      if (order.status === OrderStatus.派車中) {
        return <Button appearance="primary" block={isBlock} onClick={() => {
          onOrderAction(order, OrderActionType.Next);
        }}>
          我要接單
        </Button>
      }
    } else {
      return <></>
    }
  }

  const renderDropdownItems = () => {

    let dropdownItems = [];



    if (order.status === OrderStatus.已派車) {
      dropdownItems.push(<Dropdown.Item key="abort" onClick={() => {
        onOrderAction(order, OrderActionType.Abort);
        //setIsShowOrderDetail(false);
      }}>
        重新派車
      </Dropdown.Item>);
    }

    if (canManageOrders && order.status in { [OrderStatus.派車中]: 0, [OrderStatus.已派車]: 0 }) {
      dropdownItems.push(<Dropdown.Item key="edit" onClick={() => {
        if (order.orderID && typeof order.orderID === 'string') {
          navigate(`/orders/edit/${order.orderID}`);
        }
      }}>
        編輯訂單
      </Dropdown.Item>)

      dropdownItems.push(<Dropdown.Item key="set-related-order" onClick={async () => {
        const input = prompt("請輸入要關聯的訂單ID", order.relatedOrderID || "");
        if (input === null) {
          return;
        }

        const normalizedOrderID = input.trim();
        if (!normalizedOrderID) {
          alert("請輸入有效的訂單ID");
          return;
        }

        if (normalizedOrderID === order.orderID) {
          alert("不可關聯自己");
          return;
        }

        await handleRelatedOrderChange(normalizedOrderID);
      }}>
        {order.relatedOrderID ? "變更關聯訂單" : "設定關聯訂單"}
      </Dropdown.Item>);

      if (order.relatedOrderID) {
        dropdownItems.push(<Dropdown.Item key="clear-related-order" onClick={async () => {
          const confirmed = window.confirm(`確定要清除訂單 ${order.orderID} 的關聯訂單嗎？`);
          if (!confirmed) {
            return;
          }

          await handleRelatedOrderChange(null);
        }}>
          清除關聯訂單
        </Dropdown.Item>);
      }
    }

    dropdownItems.push(<Dropdown.Item key="delete" onClick={() => {
      const orderIdSuffix = order.orderID.slice(-5);
      const input = prompt("請輸入訂單ID末 5 碼以確認刪除");

      if (input === null) {
        return;
      }

      if (input.trim() === orderIdSuffix) {
        onOrderAction(order, OrderActionType.DeleteOrder);
      } else {
        alert("訂單ID末 5 碼不正確");
      }
    }}>
      刪除訂單
    </Dropdown.Item>);

    if (order.status === OrderStatus.載客中) {
      dropdownItems.push(<Dropdown.Item key="complete" onClick={() => {
        onOrderAction(order, OrderActionType.CompleteOrder);
      }}>標記為完成</Dropdown.Item>)
    }

    if (order.status === OrderStatus.派車中) {
      dropdownItems.push(<Dropdown.Item key="reassign" onClick={() => {
        onOrderAction(order, OrderActionType.ReAssign);
      }}>
        指派司機
      </Dropdown.Item>);
    }

    dropdownItems.push(<Dropdown.Item key="commission-payment" onClick={() => {
      if (order.orderID && typeof order.orderID === 'string') {
        navigate(`/commission-payments/${order.orderID}`);
      }
    }}>
      佣金付款
    </Dropdown.Item>);




    return dropdownItems;
  }


  const orderDisplay = () => {
    //console.log(order);
    let flightTime: Date | undefined = undefined;
    if (order.flightTime !== null) {
      flightTime = new Date(order.flightTime);
    }
    const flightStatusDisplay = flightLatestInfo ? getFlightStatusDisplay(flightLatestInfo) : null;
    //console.log(newDate.toLocaleTimeString('zh-TW', { hourCycle: 'h24' }));
    return (
      <div className="fleet-order-display">
        {isShowDetail && <div style={{ paddingBottom: '15px' }}>
          <Row>
            <Col xs={6} md={4}></Col>
            <Col xs={6} md={4}>{renderDriverActionButton(false)}</Col>
            <Col xs={12} md={16} style={{ textAlign: 'right' }}>
              {renderDropdownItems().length > 0 &&
                <Dropdown title="更多" placement="bottomEnd" icon={<App />}>
                  {renderDropdownItems()}
                </Dropdown>
              }
            </Col>
          </Row>
        </div>
        }

        <div className="fleet-order-banner">
          <Row style={{ paddingTop: '5px', paddingLeft: '15px' }}>
            <Col>
              {(order.orderType === OrderType.接機 || order.orderType === OrderType.送機) && <AirplaneFill />}
              {order.orderType === OrderType.接送 && <CarFrontFill />}
            </Col>
            <Col style={{ textAlign: 'left' }}><Text weight="extrabold">{getOrderTypeKey(order.orderType)}訂單&nbsp;{order.orderID}</Text></Col>
            {/* <Col style={{ textAlign: 'right' }}>{!isShowDetail && <CaretDownFill />}</Col> */}
          </Row>
        </div >

        <div className="fleet-order-body">

          {(order.orderType === OrderType.送機 || order.orderType === OrderType.接送) &&
            <><Row>
              <Col><Calendar /></Col>
              <Col xs={22} style={{ textAlign: 'left' }}><Text weight="bold">搭乘時間</Text><span style={{ color: 'GrayText' }}>{getDisplayDateTimeString(order.rideTime)}</span></Col>
            </Row>
              <br></br>
            </>
          }

          {order.orderType === OrderType.接機 && !isShowDetail && <>
            <Row>
              <Col><CalendarFill /></Col>
              <Col xs={22} style={{ textAlign: 'left' }}><Text weight="bold">航班時間</Text>
                <span style={{ color: 'GrayText' }}>
                  {order.flightNo} {flightTime ? flightTime.toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' }) : ''} {flightTime ? flightTime.toLocaleTimeString('zh-TW', { hourCycle: 'h23', hour: "2-digit", minute: "2-digit" }) : ''}
                </span>
              </Col>
            </Row>

            <Row>
              <Col><GeoAltFill /></Col>
              <Col xs={22} style={{ textAlign: 'left' }}><Text weight="bold">下車地址</Text>
                <span style={{ color: 'GrayText' }}>
                  {order.address}
                </span>
              </Col>
            </Row>
          </>
          }

          {order.orderType === OrderType.接送 && !isShowDetail && <>
            <Row>
              <Col><GeoAltFill /></Col>
              <Col xs={22} style={{ textAlign: 'left' }}><Text weight="bold">下車地址</Text>
                <span style={{ color: 'GrayText' }}>
                  {order.destinationAddress}
                </span>
              </Col>
            </Row>
          </>
          }

          <Row>
            <Col><GeoAltFill /></Col>
            <Col xs={22} style={{ textAlign: 'left' }}><Text weight="bold">上車地址</Text>
              <span style={{ color: 'GrayText' }}>
                {order.orderType === OrderType.接機 ? order.terminal : order.address}
              </span>

            </Col>
          </Row>



          {isShowOrderDetail && <>
            <br></br>

            <Row>
              <Col><RecordCircleFill /></Col>
              <Col xs={8} style={{ textAlign: 'left' }}><Text weight="bold">訂單狀態</Text>
                <span style={statusStyle()}>{OrderStatus[order.status]}</span>
              </Col>
              <Col><CreditCard /></Col>
              <Col xs={8} style={{ textAlign: 'left' }}><Text weight="bold">付款狀態</Text>
                <span style={statusStyle()}>{order.paymentPaid ? order.isAdminOrder ? '管理單' : '已付款' : '未付款'}</span>
              </Col>
            </Row>

            <br></br>



            {order.flightNo && order.flightNo.length > 0 && flightTime &&
              <>
                <Row>
                  <Col><Calendar /></Col>
                  <Col xs={8} style={{ textAlign: 'left' }}><Text weight="bold">{getOrderTypeKey(order.orderType)}日期</Text><span style={{ color: 'GrayText' }}>{flightTime.toLocaleString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' })}</span></Col>
                  <Col><CalendarFill /></Col>

                  <Col style={{ textAlign: 'left' }}><Text weight="bold">航班時間</Text><span style={{ color: 'GrayText' }}>
                    {order.flightNo} {flightTime.toLocaleTimeString('zh-TW', { hourCycle: 'h23', hour: "2-digit", minute: "2-digit" })}</span></Col>
                </Row>
                {flightStatusDisplay && <><br></br>
                  <Row>
                    <Col><Calendar /></Col>
                    <Col xs={8} style={{ textAlign: 'left' }}>
                      <Text weight="bold">預估航班時間</Text>
                      <span style={{ color: 'GrayText' }}>
                        {flightStatusDisplay.estimatedTime}
                      </span>
                    </Col>
                    <Col><Calendar /></Col>
                    <Col xs={8} style={{ textAlign: 'left' }}>
                      <Text weight="bold">最新航班狀態</Text>
                      <span style={{ color: flightStatusDisplay.color }}>
                        {flightStatusDisplay.remark}
                      </span>
                    </Col>
                  </Row>
                </>}
                <br></br>
              </>
            }
            <Row>
              <Col><BuildingFill /></Col>
              <Col xs={22} style={{ textAlign: 'left' }}><Text weight="bold">下車地址</Text>
                <span style={{ color: 'GrayText' }}>
                  {order.orderType === OrderType.送機 ? order.terminal : order.orderType === OrderType.接送 ? order.destinationAddress : order.address}
                </span>


              </Col>

            </Row>
            <br></br>
            {order.viaPoints && order.viaPoints.length > 0 && <>
              <Row>
                <Col><ArrowDown /></Col>
                <Col style={{ textAlign: 'left' }}>
                  <Text weight="bold">途經點</Text>
                  {order.viaPoints.map((point, index) => (
                    <div key={index} style={{ color: 'GrayText', marginTop: '4px' }}>
                      <span>{index + 1}. {point}</span>
                    </div>
                  ))}
                </Col>
              </Row>
              <br></br>
            </>}
            {order.brandId != null && <Row>
              <Col><Text weight="bold">品牌名稱</Text><span>{getOrderBrandName(order)}</span></Col>
            </Row>}
            <Row>
              <Col><CarFront /></Col>
              <Col xs={8} style={{ textAlign: 'left' }}>
                <Text weight="bold">車種</Text>
                <span style={{ color: 'GrayText' }}>{vehicleName}</span>
                {order.assignedCarModel && order.assignedCarModel.trim() !== "" && (
                  <>
                    <br></br>
                    <Text weight="bold">指定車型</Text>
                    <span style={{ color: 'GrayText', whiteSpace: 'pre-line' }}>{order.assignedCarModel}</span>
                  </>
                )}
              </Col>
              <Col><Cash /></Col>
              <Col xs={8} style={{ textAlign: 'left' }}><Text weight="bold">車資</Text><span style={{ color: 'GrayText' }}>{order.rideFee} 元</span>
                {((order.driverCommission) && (userRole === UserRole.Lead || userRole === UserRole.Driver || userRole === UserRole.Admin)) &&
                  <><br></br><Text weight="bold">司機收款</Text><span style={{ color: 'GrayText' }}>{order.driverCommission}</span></>
                }
              </Col>
            </Row>
            <br></br>
            <Row>
              <Col><SuitcaseFill /></Col>
              <Col xs={22} style={{ textAlign: 'left' }}><Text weight="bold">行李數量</Text><span style={{ color: 'GrayText', whiteSpace: 'pre-line' }}>{order.luggages ? order.luggages : 'N/A'}</span></Col>
            </Row>
            <br></br>
            <Row>
              <Col><PersonCircle /></Col>
              <Col xs={8} style={{ textAlign: 'left' }}><Text weight="bold">乘客姓名</Text><span style={{ color: 'GrayText' }}>{order.passengerName}</span></Col>
              <Col><TelephoneFill /></Col>
              <Col style={{ textAlign: 'left' }}><Text weight="bold">乘客電話</Text><span style={{ color: 'GrayText' }}>{order.passengerPhone}</span></Col>
            </Row>
            <br></br>
            <Row>
              <Col><CardHeading /></Col>
              <Col xs={8} style={{ textAlign: 'left' }}><Text weight="bold">證件號碼</Text><span style={{ color: 'GrayText' }}>{order.passengerID ? order.passengerID : 'N/A'}</span></Col>
              <Col><PeopleFill /></Col>
              <Col xs={8} style={{ textAlign: 'left' }}><Text weight="bold">乘客人數</Text><span style={{ color: 'GrayText' }}>{order.passengerAdult}乘客．{order.passengerBaby}兒童</span></Col>
            </Row>
            <br></br>
            <Row>
              <Col><Envelope /></Col>
              <Col xs={8} style={{ textAlign: 'left' }}>
                <Text weight="bold">乘客Email</Text>
                <span style={{ color: 'GrayText' }}>{order.email ? order.email : 'N/A'}</span>
              </Col>
              <Col><CardHeading /></Col>
              <Col xs={8} style={{ textAlign: 'left' }}><Text weight="bold">乘車備註</Text><span style={{ color: 'GrayText' }}>{order.luggageNote ? order.luggageNote : 'N/A'}</span></Col>
            </Row>
            <br></br>
            {(order.chatApp || (order as any).chatID) && <>
              <Row>
                <Col><ChatDotsFill /></Col>
                <Col style={{ textAlign: 'left' }}>
                  <Text weight="bold">旅客聊天資訊</Text>
                  {order.chatApp && <>
                    <br />
                    <span style={{ color: 'GrayText' }}>聊天軟體: {order.chatApp}</span>
                  </>}
                  {(order as any).chatID && <>
                    <br />
                    <span style={{ color: 'GrayText' }}>聊天 ID: {(order as any).chatID}</span>
                  </>}
                </Col>
              </Row>
              <br></br>
            </>}

            {order.meetAndGreetDetails && order.meetAndGreetDetails.trim() !== '' && <>
              <Row>
                <Col><CheckCircleFill /></Col>
                <Col xs={8} style={{ textAlign: 'left' }}>
                  <Text weight="bold">接機舉牌資訊</Text>
                  <span style={{ color: 'GrayText' }}>{order.meetAndGreetDetails}</span>
                </Col>
                <Col><CheckCircleFill /></Col>
                <Col xs={8} style={{ textAlign: 'left' }}>
                  <Text weight="bold">特殊需求</Text>
                  <div style={{ color: 'GrayText' }}>
                    {order.specialRequests && order.specialRequests.length > 0 ?
                      order.specialRequests.map((requestValue, index) => {
                        const request = SPECIAL_REQUESTS.find(r => r.value === requestValue);
                        return (
                          <div key={index}>• {request ? request.label : requestValue}</div>
                        );
                      }) : '無'}
                  </div>
                </Col>
              </Row>
              <br></br>
            </>}


            {order.supplierId && <>
              <Row>
                <Col><CardHeading /></Col>
                <Col style={{ textAlign: 'left' }}>
                  <Text weight="bold">供應商</Text>
                  <span style={{ color: 'GrayText' }}>{supplierName || `ID: ${order.supplierId}`}</span>
                </Col>
              </Row>
              <br></br>
            </>}

            {order.relatedOrderID && <>
              <Row>
                <Col><ArrowRightSquare /></Col>
                <Col style={{ textAlign: 'left' }}>
                  <Text weight="bold">關聯訂單</Text>
                  <span style={{ color: 'GrayText' }}>{order.relatedOrderID}</span>
                  <br></br>
                  <Button
                    size="xs"
                    appearance="primary"
                    onClick={() => navigate(`/orders/${order.relatedOrderID}`)}
                  >
                    查看關聯訂單
                  </Button>
                </Col>
              </Row>
              <br></br>
            </>}



            {order.driver && <>
              <Row>
                <Col><CarFront /></Col>
                <Col xs={8} style={{ textAlign: 'left' }}><Text weight="bold">司機姓名</Text><span style={{ color: 'GrayText' }}>{order.driver.driverName}</span></Col>
                <Col><TelephoneFill /></Col>
                <Col style={{ textAlign: 'left' }}><Text weight="bold">司機電話</Text><span style={{ color: 'GrayText' }}>{order.driver.phoneNumber}</span></Col>
              </Row>
              <br></br>
              <Row>
                <Col><CardText /></Col>
                <Col xs={8} style={{ textAlign: 'left' }}><Text weight="bold">車號</Text><span style={{ color: 'GrayText' }}>{order.driver.licencePlateNumber}</span></Col>
                <Col><CardText /></Col>
                <Col style={{ textAlign: 'left' }}><Text weight="bold">車輛品牌</Text><span style={{ color: 'GrayText' }}>{order.driver.carBrand}</span></Col>
              </Row>
              <br></br>
              <Row>
                <Col><CardText /></Col>
                <Col xs={8} style={{ textAlign: 'left' }}><Text weight="bold">車輛型號</Text><span style={{ color: 'GrayText' }}>{order.driver.carModel}</span></Col>
                <Col><CardText /></Col>
                <Col style={{ textAlign: 'left' }}><Text weight="bold">車輛顏色</Text><span style={{ color: 'GrayText' }}>{order.driver.carColor}</span></Col>
              </Row>
              <br></br>
              <Row>
                <Col><CardText /></Col>
                <Col xs={8} style={{ textAlign: 'left' }}><Text weight="bold">銀行代碼</Text><span style={{ color: 'GrayText' }}>{order.driver.bankID || 'N/A'}</span></Col>
                <Col><CardText /></Col>
                <Col style={{ textAlign: 'left' }}><Text weight="bold">銀行帳號</Text><span style={{ color: 'GrayText' }}>{order.driver.bankAccount || 'N/A'}</span></Col>
              </Row>
              <br></br>

            </>}

            <Row>
              <Col><GeoAltFill /></Col>
              <Col style={{ textAlign: 'left' }}><Text weight="bold">建單時間</Text><span style={{ color: 'GrayText' }}>{getDisplayDateTimeString(order.createdAt)}</span></Col>
            </Row>

            <br></br>
            {order.creatorUID && <>
              <Row>
                <Col><GeoAltFill /></Col>
                <Col style={{ textAlign: 'left' }}><Text weight="bold">建單者</Text><span style={{ color: 'GrayText' }}>{creatorName || order.creatorUID}</span></Col>
              </Row>
            </>}


          </>
          }
        </div >
      </div>
    )
  }

  return (order ? orderDisplay() : null);

}
